import { Pencil, Trash2 } from "lucide-react";
import { useLocale, useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { SubscriptionPlanView } from "@/lib/api/types";
import { SUBSCRIPTION_PLAN_TYPE } from "@/lib/api/types";
import { Badge, Button, StatusDot, Tag } from "@/shared/rd";
import { planTypeBadge, planTypeLabelKey, planTypeTone } from "../lib/plan-display";

type Props = {
  plan: SubscriptionPlanView;
  canEdit: boolean;
  onEdit: () => void;
  onDelete: () => void;
};

export function SubscriptionPlanCard({ plan, canEdit, onEdit, onDelete }: Props) {
  const t = useTranslations();
  const locale = useLocale();
  const isPerOrderCredit = plan.type === SUBSCRIPTION_PLAN_TYPE.PER_ORDER_CREDIT;
  // A per-order plan has no period price and no period — the columns carry
  // stored defaults nothing reads. Showing them would read "EGP 0 / 30 days"
  // as though the plan meant it. A Free plan has neither either.
  const showsPeriod = !isPerOrderCredit && plan.type !== SUBSCRIPTION_PLAN_TYPE.FREE;

  const formatAmount = (
    amount: number,
    // Per-order prices are small and often fractional, so they keep their
    // decimals while plan/period amounts stay rounded.
    withDecimals = false,
  ) => {
    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: plan.currency || "EGP",
        maximumFractionDigits: withDecimals ? 2 : 0,
      }).format(amount);
    } catch {
      return `${amount} ${plan.currency ?? ""}`.trim();
    }
  };

  return (
    <article
      className="rd-card"
      style={{ padding: "16px 18px", display: "flex", flexDirection: "column", gap: 12, opacity: plan.isActive ? 1 : 0.65 }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <Badge id={planTypeBadge(plan.type)} size={36} />
        <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.3, flex: 1, minWidth: 0 }}>
          <span dir="rtl" style={{ fontSize: 16, fontWeight: 600, textAlign: "start" }}>
            {plan.nameAr}
          </span>
          <span dir="auto" style={{ fontSize: 13, color: "var(--rd-text-secondary)" }}>
            {plan.nameEn || plan.nameAr}
          </span>
        </div>
        <StatusDot tone={plan.isActive ? "green" : "grey"}>
          {plan.isActive ? t(keys.common.active) : t(keys.common.inactive)}
        </StatusDot>
      </div>

      <dl className="rd-kv" style={{ gridTemplateColumns: "minmax(0,1fr) auto", rowGap: 6, fontSize: 13 }}>
        <dt>{t(keys.admin.subscriptionPlanType)}</dt>
        <dd>
          <Tag tone={planTypeTone(plan.type)}>{t(planTypeLabelKey(plan.type))}</Tag>
        </dd>
        {isPerOrderCredit && plan.perOrderAmount !== null ? (
          <>
            <dt>{t(keys.admin.subscriptionPlanPerOrderAmount)}</dt>
            <dd className="rd-num" style={{ fontWeight: 600 }}>
              {formatAmount(plan.perOrderAmount, true)}
            </dd>
          </>
        ) : null}
        {showsPeriod ? (
          <>
            <dt>{t(keys.admin.subscriptionPlanAmount)}</dt>
            <dd className="rd-num" style={{ fontWeight: 600 }}>
              {formatAmount(plan.amount)}
            </dd>
            <dt>{t(keys.admin.subscriptionPlanPeriodDays)}</dt>
            <dd className="rd-num">{plan.periodDays}</dd>
          </>
        ) : null}
      </dl>

      {canEdit ? (
        <div style={{ display: "flex", gap: 6, borderTop: "1px solid var(--rd-line-row)", paddingTop: 10, marginTop: "auto" }}>
          <Button variant="ghost" size="sm" icon={Pencil} onClick={onEdit}>
            {t(keys.common.edit)}
          </Button>
          <Button variant="danger" size="sm" icon={Trash2} onClick={onDelete}>
            {t(keys.common.delete)}
          </Button>
        </div>
      ) : null}
    </article>
  );
}
